import { FetchPolicy, gql } from '@apollo/client';
import {
  ActionStatus,
  ClaimEditChangeOptions,
  ClaimEditData,
  ClaimEditField,
  ClaimEditValues,
  claims,
  configuration,
} from 'api/endpoints';
import { clientClaims } from 'api/endpoints/clientClaims';
import useGraphql from 'api/graphql/useGraphql';
import { useAppDispatch, useAppSelector } from 'hooks/reducerHooks';
import _ from 'lodash';
import { GridColumnsSectionType } from 'pages/ClaimsContractsToolPage/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ClaimReviewerData } from 'reducer/claimReviewerReducer';
import { fetchUserDashboardTasksThunk } from 'reducer/thunks/dashboard-thunks';
import { useApplicationUser } from 'utils/AppInitializer/hooks/useApplicationUser';
import { useAppUserType } from 'utils/hooks/useAppUserType';
import { survivingClaimNumberUniq } from '../sections/SingleClaimOverview/util/valueFormatter';

import {
  getRelatedClaims,
  RelatedClaimsData,
} from 'pages/SingleClaimPage/sections/SingleClaimOverview/util/getRelatedClaims';
import { useResolutionExhibitTypeOptions } from '../../../hooks/useResolutionExhibitTypeOptions';
import { useEditableField } from '../sections/SingleClaimOverview/hook/useEditableField';

const idColumns = [
  'id',
  'originalDebtorId',
  'currentDebtorId',
  'proposedDebtorId',
  'alixPartnersDebtorId',
  'clientDebtorId',
  'counselDebtorId',
  'pORDebtorId',
  'majorCategoryId',
  'minorCategoryId',
  'actionNameId',
  'resolutionTypeId',
  'actionSubStatusId',
];

export type ChangeOptionsHook = [
  ClaimEditChangeOptions,
  React.Dispatch<React.SetStateAction<ClaimEditChangeOptions>>,
];

const DEFAULT_CHANGE_OPTIONS: ClaimEditChangeOptions = { comment: '' };

type ConfirmationModal = {
  message: string;
  onConfirm: (confirm: boolean) => void;
};

export type EditFieldFunc = <F extends ClaimEditField>(
  field: F,
  value: ClaimEditValues[F],
) => void;

export interface EditClaimHook {
  loading: boolean;
  isEdited: boolean;
  claim: ClaimEditValues | undefined;
  changeOptionsHook: ChangeOptionsHook;
  isSaving: boolean;
  claimId: number;
  confirmationModal: ConfirmationModal | undefined;
  reloadData: () => void;
  editField: EditFieldFunc;
  saveButtonClick: () => void;
  relatedClaimsData: RelatedClaimsData;
  isEditable: (
    field: string,
    column?: string,
    actionStatus?: ActionStatus | undefined,
  ) => boolean;
}

export function useSingleClaimProvider(): EditClaimHook {
  const dispatch = useAppDispatch();
  const user = useApplicationUser();
  const [isEdited, setEdited] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmationModal, setConfirmationModal] = useState<ConfirmationModal>();

  const changeOptionsHook = useState(DEFAULT_CHANGE_OPTIONS);
  const [changeOptions, setChangeOptions] = changeOptionsHook;
  const [claimEdits, setClaimEdits] = useState<Partial<ClaimEditValues>>({});

  const [originalClaim, setOriginalClaim] = useState<ClaimEditValues | undefined>();
  const [client, authorized] = useGraphql();

  const { isClient, isApUser } = useAppUserType();
  const { showMine } = useAppSelector((s) => s.userDashboardTasks.slice);
  const [relatedClaimsData, setRelatedClaimsData] = useState<RelatedClaimsData>(
    {} as RelatedClaimsData,
  );
  const resolutionOptions = useResolutionExhibitTypeOptions();

  const claimReviewer = useAppSelector((state) => state.claimReviewer);

  const claim = useMemo(
    () => (originalClaim ? { ...originalClaim, ...claimEdits } : undefined),
    [claimEdits, originalClaim],
  );

  const { isEditable } = useEditableField({ claim });

  let { id } = useParams();
  const claimId = Number(id);

  const loadClaims = useCallback(
    async (policy?: FetchPolicy) => {
      setLoading(true);
      const params = {
        query: await queryGetClaimById(),
        variables: { id: claimId },
        fetchPolicy: policy,
      };
      const result = await client.query(params);
      const item = extractClaimItem(result.data);
      setOriginalClaim(item);
      setLoading(false);

      return item;
    },
    [claimId, client],
  );

  const loadRelatedClaims = useCallback(
    async (item?: ClaimEditValues | undefined) => {
      setLoading(true);
      const relatedClaims = await getRelatedClaims(
        item?.parentClaimNumber,
        item?.referenceNumber,
        client,
      );
      setRelatedClaimsData(relatedClaims ?? {});
      setLoading(false);
    },
    [client],
  );

  const loadAll = useCallback(async () => {
    const claim = await loadClaims();
    loadRelatedClaims(claim);
  }, [loadClaims, loadRelatedClaims]);

  useEffect(() => {
    if (!authorized) return;
    loadAll();
  }, [authorized]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadData = useCallback(async () => {
    await loadClaims('network-only');
    setClaimEdits({});
  }, [loadClaims]);

  const editField = useCallback<EditFieldFunc>(
    (field, value) => {
      const existingValue = _.get(claim, field);
      if (value === existingValue) return;

      let newValue: Partial<ClaimEditValues> = { [field]: value };

      if (field === 'resolutionTypeId') {
        newValue = resolutionOptions.getResolutionTypeChangedValues(
          value as number,
          claim,
        );
      }

      if (field === 'actionSubStatusId') {
        newValue = resolutionOptions.getResolutionSubTypeChangedValues(
          claim?.resolutionTypeId,
          value as number,
          claim,
        );
      }

      const newClaim = { ...claimEdits, ...newValue };

      setClaimEdits(newClaim);
      setEdited(true);
    },
    [claim, claimEdits, resolutionOptions],
  );

  const saveData = useCallback(
    async (data: ClaimEditData) => {
      if (isSaving) return;

      setSaving(true);
      if (isApUser) {
        await claims.edit(data);
      } else {
        await clientClaims.edit(data);
      }

      setSaving(false);
      setChangeOptions(DEFAULT_CHANGE_OPTIONS);

      reloadData();

      await loadClaims('network-only');
      if (user?.data) {
        dispatch(fetchUserDashboardTasksThunk({ user: user.data, showMine }));
      }
    },
    [
      dispatch,
      isApUser,
      isSaving,
      loadClaims,
      reloadData,
      setChangeOptions,
      showMine,
      user.data,
    ],
  );

  const validateData = useCallback(async (data: ClaimEditData) => {
    setSaving(true);
    const warningsList = await claims.warnings(data);
    const result = warningsList?.[0];
    setSaving(false);

    return result;
  }, []);

  const saveButtonClick = useCallback(async () => {
    if (!isEdited || isSaving || !originalClaim) return;

    const dataToSave = createDataToSave(
      originalClaim,
      claimEdits,
      claimReviewer.data,
      changeOptions,
    );

    if (isClient) {
      saveData(dataToSave);
      return;
    }

    const validationError = await validateData(dataToSave);

    if (!validationError) {
      saveData(dataToSave);
      return;
    }

    setConfirmationModal({
      message: validationError,
      onConfirm: (confirmed: boolean) => {
        if (confirmed) saveData(dataToSave);
        setConfirmationModal(undefined);
      },
    });
  }, [
    isEdited,
    isSaving,
    originalClaim,
    claimEdits,
    claimReviewer.data,
    changeOptions,
    isClient,
    validateData,
    saveData,
  ]);

  return {
    loading,
    isEdited,
    isSaving,
    claim,
    changeOptionsHook,
    claimId,
    confirmationModal,
    reloadData,
    isEditable,
    editField,
    saveButtonClick,
    relatedClaimsData,
  };
}

const createDataToSave = (
  originalClaim: ClaimEditValues,
  claimEdits: Partial<ClaimEditValues>,
  reviewers: ClaimReviewerData,
  changeOptions: ClaimEditChangeOptions,
): ClaimEditData => {
  //todo: we have to refactor the next solution
  const values: ClaimEditValues = {
    ...(claimEdits as ClaimEditValues),
    survivingClaimNumber: survivingClaimNumberUniq(claimEdits.survivingClaimNumber),
    claimReviewerUserGroupIds: reviewers.userGroupReviewers,
    claimReviewerUserIds: reviewers.userReviewers,
  };

  return {
    claimId: originalClaim.id,
    values,
    changeOptions,
  };
};

function extractClaimItem(data: any): ClaimEditValues | undefined {
  const item: ClaimEditValues | undefined = data?.claims?.items?.[0];
  if (!item) return undefined;

  return {
    ...item,
    _fetchTimestamp: new Date().toISOString(),
  };
}

const queryGetClaimById = async () => {
  const allGridColumns =
    (await configuration.getGridColumns(GridColumnsSectionType.Claim)) ?? [];
  const filteredColumns = allGridColumns
    // We need to filter non-dynamic cols and only enabled dynamic fields
    .filter((el) => !(el.propertyName.includes('custom') && !el.enabled))
    .map((c) => c.propertyName)
    .sort();

  const requestCols = [...idColumns, ...filteredColumns];

  return gql`
      query GetClaimById($id: Int!) {
        claims(where: {
          id: { eq: $id }
        }) {
          totalCount
          items {
            ${requestCols.join(' ')}
          }
        }
      }
    `;
};
